import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, Globe } from "lucide-react";
import { company } from "../lib/data";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/tools", label: "Tools" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#0a0a0a]/85 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-2.5 group"
          aria-label={company.name}
        >
          <span className="relative inline-flex items-center gap-1">
            <span className="relative inline-flex h-9 w-9 items-center justify-center">
              <span className="absolute inset-0 bg-[#dc2626] -skew-x-12" />
              <span className="relative font-display font-black text-white text-lg">
                D
              </span>
            </span>
            <span className="relative inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#1d4ed8] text-white shadow-[0_0_0_2px_#0a0a0a,0_0_0_3px_rgba(29,78,216,0.35)] group-hover:rotate-12 transition-transform duration-500">
              <Globe className="h-5 w-5" strokeWidth={2} />
            </span>
          </span>
          <div className="hidden sm:flex leading-tight flex-col">
            <span className="font-display font-bold text-white tracking-tight text-base">
              DVP DIGI WORLD
            </span>
            <span className="text-[10px] uppercase tracking-[0.25em] text-neutral-400">
              Digi World
            </span>
          </div>
          <span className="sm:hidden font-display font-bold text-white tracking-tight text-base">
            DVP DIGI WORLD
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative px-4 py-2 text-sm font-medium transition-colors ${
                  isActive ? "text-white" : "text-neutral-300 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  {isActive && (
                    <span className="absolute left-4 right-4 -bottom-0.5 h-0.5 bg-[#dc2626]" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={company.phoneHref}
            className="text-sm text-neutral-300 hover:text-white transition-colors font-mono"
            data-testid="nav-phone"
          >
            {company.phone}
          </a>
          <Link
            to="/request-access"
            data-testid="nav-cta-request-access"
            className="inline-flex items-center gap-2 bg-[#dc2626] hover:bg-[#ef4444] text-white px-4 py-2.5 text-sm font-semibold transition-colors"
          >
            Request Access
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="md:hidden flex items-center gap-2">
          <Link
            to="/request-access"
            data-testid="nav-mobile-quick-cta"
            className="inline-flex items-center bg-[#dc2626] hover:bg-[#ef4444] text-white px-3 py-2 text-xs font-semibold"
          >
            Apply
          </Link>
          <button
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            className="p-2 text-white"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-white/10 bg-[#0a0a0a]/95 backdrop-blur-xl">
          <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3 py-3 text-base border-l-2 ${
                    isActive
                      ? "border-[#dc2626] text-white bg-white/5"
                      : "border-transparent text-neutral-300"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/request-access"
              data-testid="nav-mobile-cta"
              className="mt-2 text-center bg-[#dc2626] hover:bg-[#ef4444] text-white px-4 py-3 font-semibold"
            >
              Request Access
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
